import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';

const VerifyEmailPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [status, setStatus] = useState('loading'); // loading, success, error, no-token
    const [message, setMessage] = useState('');
    
    useEffect(() => {
        if (!token) {
            setStatus('no-token');
            setMessage('Nu a fost furnizat un token de verificare.');
            return;
        }
        
        verifyEmail();
    }, [token]);
    
    const verifyEmail = async () => {
        try {
            setStatus('loading');
            const response = await api.get(`/auth/verify-email?token=${token}`);
            setStatus('success');
            setMessage(response.data.message || 'Email verificat cu succes!');
        } catch (error) {
            setStatus('error');
            setMessage(error.response?.data?.detail || 'Eroare la verificarea email-ului.');
        }
    };
    
    return (
        <div className="min-h-screen bg-linear-to-b from-gray-50 to-white flex items-center justify-center p-4">
            <Card className="w-full max-w-md shadow-xl border-0">
                <CardHeader className="text-center pb-2">
                    <div className="mx-auto mb-4">
                        {status === 'loading' && (
                            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                            </div>
                        )}
                        {status === 'success' && (
                            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                        )}
                        {(status === 'error' || status === 'no-token') && (
                            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                                <XCircle className="w-8 h-8 text-red-600" />
                            </div>
                        )}
                    </div>
                    <CardTitle className="text-2xl font-bold">
                        {status === 'loading' && 'Se verifică...'}
                        {status === 'success' && 'Email Verificat!'}
                        {status === 'error' && 'Verificare Eșuată'}
                        {status === 'no-token' && 'Link Invalid'}
                    </CardTitle>
                </CardHeader>
                <CardContent className="text-center space-y-6">
                    <p className="text-gray-600">
                        {message}
                    </p>
                    
                    {status === 'success' && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-500">
                                Acum poți accesa toate funcțiile platformei.
                            </p>
                            <Button 
                                onClick={() => navigate('/dashboard')}
                                className="w-full bg-blue-600 hover:bg-blue-700"
                            >
                                Continuă către Dashboard
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    )}
                    
                    {status === 'error' && (
                        <div className="space-y-3">
                            <p className="text-sm text-gray-500">
                                Link-ul poate fi expirat sau deja folosit.
                            </p>
                            <Link to="/login">
                                <Button variant="outline" className="w-full">
                                    <Mail className="w-4 h-4 mr-2" />
                                    Înapoi la autentificare
                                </Button>
                            </Link>
                        </div>
                    )}
                    
                    {status === 'no-token' && (
                        <Link to="/">
                            <Button variant="outline" className="w-full">
                                Înapoi la pagina principală
                            </Button>
                        </Link>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default VerifyEmailPage;
